// FE#199 — Knowledge Base tool silently dropped on save.
//
// An Agent node can reach save with a *configured* Knowledge Base in
// `data.toolSettings` (the tool-settings dialog patches `toolSettings[toolName]`
// on save) while the KB tool name is missing from `data.tools`. `saveBotThunk`
// serializes the nodes verbatim (`JSON.stringify({ nodes, edges })`), so the
// persisted flow carries `tools: []` and the deployed agent has no Knowledge
// Base tool — even though the user selected knowledge bases for it.
//
// The reconciliation below runs at serialization time: if `toolSettings` names a
// *configured* KB tool (at least one selected knowledge base) that is absent
// from `tools`, add it back. Pure + idempotent so it is safe to run on every
// save. It only ever re-adds a tool that is genuinely configured — an orphaned
// or empty settings entry does NOT imply the tool (see `handleRemoveTool`, which
// strips a tool's settings on removal so removal stays authoritative and this
// reconciler can never resurrect a deliberately-removed KB tool).

/**
 * Collapse a tool name to its identity the same way tool-settings-dialog does,
 * so 'Knowledge Base', 'knowledge_base', 'knowledgeBase' all map to one key.
 */
const normalizeToolName = (name: unknown): string =>
  (typeof name === 'string' ? name : '').toLowerCase().replace(/[\s_-]/g, '');

const isKnowledgeBaseTool = (name: unknown): boolean =>
  normalizeToolName(name) === 'knowledgebase';

/**
 * A KB settings object "implies the tool" only when it actually selects at
 * least one knowledge base. An empty `{}` or `{ knowledgeBaseIds: [] }` does
 * not — that is either an untouched default or an orphan left by removal.
 */
const isConfiguredKbSettings = (settings: any): boolean =>
  !!settings &&
  Array.isArray(settings.knowledgeBaseIds) &&
  settings.knowledgeBaseIds.length > 0;

export interface AgentToolData {
  tools?: string[];
  toolSettings?: Record<string, any>;
  [key: string]: any;
}

/**
 * Reconcile a single node's `data`: ensure every *configured* KB tool named in
 * `toolSettings` is present in `tools`. Returns the SAME reference when nothing
 * changed (so callers can cheaply detect a no-op), a shallow copy otherwise.
 */
export function reconcileAgentToolsFromSettings<T extends AgentToolData>(data: T): T {
  const toolSettings = data?.toolSettings;
  if (!toolSettings || typeof toolSettings !== 'object') return data;

  const tools = Array.isArray(data.tools) ? data.tools : [];
  const present = new Set(tools.map(normalizeToolName));
  const missing: string[] = [];

  for (const [toolName, settings] of Object.entries(toolSettings)) {
    if (
      isKnowledgeBaseTool(toolName) &&
      isConfiguredKbSettings(settings) &&
      !present.has(normalizeToolName(toolName))
    ) {
      missing.push(toolName);
      present.add(normalizeToolName(toolName));
    }
  }

  if (missing.length === 0) return data;
  return { ...data, tools: [...tools, ...missing] };
}

/**
 * Apply the reconciliation across a full nodes array — used by `saveBotThunk`
 * before it builds `flowJSON`. Non-agent / dataless nodes pass through
 * untouched. Returns the SAME array reference when no node changed.
 */
export function reconcileNodesAgentTools<N extends { data?: any }>(nodes: N[]): N[] {
  if (!Array.isArray(nodes)) return nodes;
  let changed = false;
  const out = nodes.map((node) => {
    if (!node?.data || typeof node.data !== 'object') return node;
    const reconciled = reconcileAgentToolsFromSettings(node.data);
    if (reconciled === node.data) return node;
    changed = true;
    return { ...node, data: reconciled };
  });
  return changed ? out : nodes;
}
